import { useCallback, useState, type FormEvent } from 'react'
import { phrases } from '../state/phrases'
import { useVoiceInput } from '../state/useVoiceInput'
import type { SaleEngine } from '../state/useSaleEngine'

/**
 * The languages a customer may name for the microphone, written in their own script so somebody who reads
 * only that script can find it.
 *
 * "Detect" is the default and is right for most people, including anyone mixing English with their own
 * language mid-sentence. The list exists for one reason: spoken Hindi and Urdu are near-identical, so an
 * auto-detected Hindi sentence comes back in Devanagari on one recording and Arabic script on the next.
 * Both are correct; one of them is unreadable to the person who just spoke.
 *
 * Only languages the transcription model actually accepts are offered. Bhojpuri and Odia are not on it -
 * listing them would promise something that silently transcribes as a different language.
 */
const SPEECH_LANGUAGES: { code: string; label: string }[] = [
  { code: "", label: "Detect" },
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "bn", label: "বাংলা" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "ml", label: "മലയാളം" },
  { code: "mr", label: "मराठी" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "ur", label: "اردو" },
  { code: "as", label: "অসমীয়া" },
]

/** Remembered per browser, because somebody who speaks Tamil this time speaks Tamil next time too. */
const LANGUAGE_KEY = "sellgpt.speechLanguage.v1"

/** mm:ss, so a customer can see they are near the one-minute ceiling rather than being cut off by it. */
function formatSeconds(total: number): string {
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

/** The starter chips from the design. They are shown only while there is nothing to answer yet. */
const STARTERS = [
  { label: '📱 Sell a phone', text: 'I want to sell my phone' },
  { label: '💻 Sell a laptop', text: 'I want to sell my laptop' },
  { label: '▤ Sell a tablet', text: 'I want to sell my tablet' },
  { label: '✦ Something else', text: 'What else can I sell?' },
]

/**
 * The composer, fixed at the bottom of the conversation column.
 *
 * It is never hidden while a sale is in progress, even on a step that is answered by clicking: a customer
 * who wants to say "actually it's the 256GB one" must be able to, and the placeholder changes to say what
 * the current step expects rather than leaving them to guess.
 */
export default function Composer({ engine }: { engine: SaleEngine }) {
  const [text, setText] = useState('')
  const { sale, question, busy, thinking } = engine

  /**
   * A transcript lands IN the composer; it is never sent on the customer's behalf. Speech recognition
   * mishears model names more than anything else - "Redmi Note 12" comes back as "Retme note twelve" often
   * enough - and a wrong name searches the catalogue for a device that does not exist. They read it, fix
   * it, send it.
   *
   * Appended rather than replacing, so someone who typed half a sentence and then spoke the rest keeps both.
   */
  const onTranscript = useCallback(
    (spoken: string, language?: string | null) => {
      setText((current) => (current.trim() ? `${current.trim()} ${spoken}` : spoken))
      if (language) engine.noteLanguage(language)
    },
    [engine],
  )

  const [speechLanguage, setSpeechLanguage] = useState(() => {
    try {
      return window.localStorage.getItem(LANGUAGE_KEY) ?? ""
    } catch {
      return "" // storage blocked; detection is the default anyway
    }
  })

  const voice = useVoiceInput(onTranscript, engine.config?.voiceInputAvailable ?? false, speechLanguage || undefined)

  const sending = busy || thinking
  const finished = sale.step === 'done'

  const placeholder = finished
    ? 'Start a new sale from the sidebar'
    : sale.step === 'pincode'
      ? 'Enter a 6-digit pincode'
      : question?.placeholder ?? phrases(engine.language).composerPlaceholder

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    const value = text
    setText('')
    void engine.send(value)
  }

  return (
    <div className="entry-area" id="entryArea">
      {sale.error && (
        <p className="error-note" role="alert">
          <span>{sale.error}</span>
          <button type="button" aria-label="Dismiss" onClick={engine.clearError}>
            ✕
          </button>
        </p>
      )}

      {sale.messages.length === 0 && (
        <div className="suggestions">
          {STARTERS.map((starter) => (
            <button key={starter.label} type="button" onClick={() => void engine.send(starter.text)}>
              {starter.label}
            </button>
          ))}
        </div>
      )}

      {/* Only while the microphone is doing something. A permanent strip of status text under the composer
          would be noise on every other turn. */}
      {(voice.state === 'asking' || voice.state === 'recording' || voice.state === 'transcribing' || voice.error) && (
        <p className={`voice-status${voice.error ? ' failed' : ''}`} role="status">
          {voice.state === 'asking' ? (
            <>Allow microphone access when your browser asks…</>
          ) : voice.state === 'recording' ? (
            <>
              <span className="rec-dot" aria-hidden="true" />
              Listening… <span className="rec-time">{formatSeconds(voice.seconds)}</span>
              <button type="button" className="linkish" onClick={voice.cancel}>
                Cancel
              </button>
            </>
          ) : voice.state === 'transcribing' ? (
            <>Writing down what you said…</>
          ) : (
            voice.error
          )}
        </p>
      )}

      {voice.state !== 'unsupported' && (
        <label className="speech-language" htmlFor="sg-speech-language">
          <span className="sr-only">Language you will speak</span>
          <span aria-hidden="true">🗣</span>
          <select
            id="sg-speech-language"
            value={speechLanguage}
            onChange={(event) => {
              setSpeechLanguage(event.target.value)
              try {
                window.localStorage.setItem(LANGUAGE_KEY, event.target.value)
              } catch {
                /* storage blocked; the choice still applies for this visit */
              }
            }}
          >
            {SPEECH_LANGUAGES.map((option) => (
              <option key={option.code || 'auto'} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <form className="composer" onSubmit={onSubmit}>
        <input
          aria-label="Tell us what you want to sell"
          placeholder={placeholder}
          autoComplete="off"
          inputMode={sale.step === 'pincode' ? 'numeric' : 'text'}
          value={text}
          disabled={finished}
          onChange={(event) => setText(event.target.value)}
        />
        {/* Hidden, not disabled, when the server cannot transcribe or the browser cannot record: a
            microphone that is visibly there and does nothing is worse than no microphone. */}
        {voice.state !== 'unsupported' && (
          <button
            type="button"
            className={`mic${voice.state === 'recording' ? ' on' : ''}`}
            aria-label={voice.state === 'recording' ? 'Stop recording' : 'Speak instead of typing'}
            aria-pressed={voice.state === 'recording'}
            disabled={finished || voice.state === 'transcribing' || voice.state === 'asking'}
            onClick={voice.state === 'recording' ? voice.stop : voice.start}
          >
            {/* Drawn rather than typed: the microphone emoji renders as a thin outline on Windows, a
                different colour on Android and not at all on some devices. A filled path is the same
                bold shape everywhere and takes the button's own colour. */}
            {voice.state === 'recording' ? (
              <span className="rec-square" aria-hidden="true" />
            ) : (
              <svg className="mic-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 14.5a3.5 3.5 0 0 0 3.5-3.5V6a3.5 3.5 0 1 0-7 0v5a3.5 3.5 0 0 0 3.5 3.5Z" />
                <path d="M18.5 11a.9.9 0 0 0-1.8 0 4.7 4.7 0 0 1-9.4 0 .9.9 0 0 0-1.8 0 6.5 6.5 0 0 0 5.6 6.4V20h-2a.9.9 0 0 0 0 1.8h5.8a.9.9 0 0 0 0-1.8h-2v-2.6a6.5 6.5 0 0 0 5.6-6.4Z" />
              </svg>
            )}
          </button>
        )}
        <button className="send" type="submit" aria-label="Send message" disabled={sending || finished || !text.trim()}>
          ↑
        </button>
      </form>

      <p className="under-note">
        Prices come from Cashit's live valuation and are confirmed after inspection.
      </p>
    </div>
  )
}
